import React, { useState } from 'react';
import { SideBarFilter } from './side-bar-filter/side-bar-filter';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-purple/theme.css';
import './general-attendance.css';
import { ReportHeader } from '../../header/report-header';
import { CustomTag } from '../../../../components/CustomTag/CustomTag';
import Colors from '../../../../utils/colors';
import { GeneralAttendanceSideBarFilterDataModel } from './side-bar-filter/general-attendance-side-bar-filter-data-model';
import { generalAttendanceOperations } from './opearations/general-attendance-operations';
import { ReportGeneralAttendanceType } from '../../../../api/types/reports/education/report-general-attendance.type';
import { getProgramStudentStatusDisplayText } from '../../../../api/types/programs/students/program-student-status';

export const GeneralAttendance = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ReportGeneralAttendanceType[]>([]);

  const getAbsenceStyle = (absencePercentage: number) => {
    if (absencePercentage >= 25)
      return { color: Colors.dangerText2, fontWeight: 'bold' };
    if (absencePercentage >= 15)
      return { color: Colors.warningYelloText, fontWeight: 'bold' };

    return { color: Colors.approveText2, fontWeight: 'bold' };
  };

  const getAttendanceStyle = (attendancePercentage: number) => {
    if (attendancePercentage < 75)
      return { color: Colors.dangerText, fontWeight: 'bold' };
    if (attendancePercentage < 85)
      return { color: Colors.warningYelloText, fontWeight: 'bold' };
    return { color: Colors.approveText2, fontWeight: 'bold' };
  };

  const getStatusSeverity = (status: string) => {
    const severityMap: {
      [key: string]: 'success' | 'warning' | 'danger' | 'info';
    } = {
      STUDYING: 'success',
      REGISTERED: 'info',
      COMPLETED: 'success',
      GRADUATED: 'success',
      LEFT: 'danger',
      CANCELLED: 'danger',
    };
    return severityMap[status] || 'info';
  };

  // Mock data for demonstration
  const attendanceData = [
    {
      branch: 'ירושלים',
      name: 'ישראל ישראלי',
      id: '123456789',
      program: 'הנדסת תוכנה',
      startYear: '2023',
      courseName: 'מבוא לתכנות',
      absencePercentage: 15,
      totalAttendancePercentage: 85,

      studentStatus: 'פעיל',
    },
    {
      branch: 'תל אביב',
      name: 'יעקב כהן',
      id: '987654321',
      program: 'מדעי המחשב',
      startYear: '2022',
      courseName: 'אלגוריתמים',
      absencePercentage: 8,
      totalAttendancePercentage: 92,
      studentStatus: 'פעיל',
    },
    {
      branch: 'חיפה',
      name: 'שרה לוי',
      id: '456789123',
      program: 'הנדסת תוכנה',
      startYear: '2023',
      courseName: 'תכנות מונחה עצמים',
      absencePercentage: 20,
      totalAttendancePercentage: 80,
      studentStatus: 'בהשעיה',
    },
    {
      branch: 'באר שבע',
      name: 'דוד כהן',
      id: '789123456',
      program: 'מערכות מידע',
      startYear: '2021',
      courseName: 'בסיסי נתונים',
      absencePercentage: 5,
      totalAttendancePercentage: 95,
      studentStatus: 'פעיל',
    },
    {
      branch: 'ירושלים',
      name: 'רחל גולדברג',
      id: '321654987',
      program: 'הנדסת תוכנה',
      startYear: '2022',
      courseName: 'אבטחת מידע',
      absencePercentage: 12,
      totalAttendancePercentage: 88,
      studentStatus: 'פעיל',
    },
    {
      branch: 'תל אביב',
      name: 'משה פרץ',
      id: '147258369',
      program: 'מדעי המחשב',
      startYear: '2023',
      courseName: 'מתמטיקה בדידה',
      absencePercentage: 25,
      totalAttendancePercentage: 75,
      studentStatus: 'בהתראה',
    },
    {
      branch: 'חיפה',
      name: 'מרים אברהם',
      id: '963852741',
      program: 'מערכות מידע',
      startYear: '2021',
      courseName: 'ניתוח מערכות',
      absencePercentage: 10,
      totalAttendancePercentage: 90,
      studentStatus: 'פעיל',
    },
    {
      branch: 'באר שבע',
      name: 'יוסף דוידוב',
      id: '852963741',
      program: 'הנדסת תוכנה',
      startYear: '2022',
      courseName: 'רשתות תקשורת',
      absencePercentage: 18,
      totalAttendancePercentage: 82,
      studentStatus: 'פעיל',
    },
    {
      branch: 'ירושלים',
      name: 'חנה רוזנברג',
      id: '741852963',
      program: 'מדעי המחשב',
      startYear: '2023',
      courseName: 'מבני נתונים',
      absencePercentage: 30,
      totalAttendancePercentage: 70,
      studentStatus: 'בהשעיה',
    },
    {
      branch: 'תל אביב',
      name: 'אברהם לוין',
      id: '369147852',
      program: 'הנדסת תוכנה',
      startYear: '2021',
      courseName: 'פיתוח אפליקציות',
      absencePercentage: 7,
      totalAttendancePercentage: 93,
      studentStatus: 'פעיל',
    },
  ];

  const handleSearch = async (
    filters: GeneralAttendanceSideBarFilterDataModel,
  ) => {
    await generalAttendanceOperations(filters, setLoading, setData);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח נוכחות כללית"
        description="דו״ח זה מציג מידע מקיף על נוכחות התלמידים בכל התכניות והקורסים. הדו״ח כולל נתוני נוכחות פרטניים לכל תלמיד, כולל אחוזי היעדרות בקורסים ספציפיים ואחוזי נוכחות כוללים בתכנית."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'מעקב אחר נוכחות והתמדה' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="education"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <SideBarFilter onSearch={handleSearch} />
        </div>

        <div style={{ flex: 1 }}>
          <DataTable
            value={data}
            rows={10}
            rowHover
            loading={loading}
            rowClassName={() => 'cursor-pointer'}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="id"
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{
              direction: 'rtl',
            }}
            className="general-attendance-table"
          >
            <Column
              field="branchName"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="studentName"
              header="שם"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: ReportGeneralAttendanceType) =>
                `${rowData.firstName} ${rowData.lastName}`
              }
            />
            <Column
              field="nationalId"
              header="ת.ז"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="abstractProgramName"
              header="תכנית לימודים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="programStartYear"
              header="שנת התחלה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="courseName"
              header="שם קורס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="missedCount"
              header="מספר היעדרויות"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="missedRatio"
              header="אחוז היעדרויות בקורס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: ReportGeneralAttendanceType) => (
                <span style={getAbsenceStyle(Number(rowData.missedRatio))}>
                  {`${Number(rowData.missedRatio).toFixed(1)}%`}
                </span>
              )}
            />
            {/*            <Column
              field="courseAbsenceRate"
              header="אחוז היעדרויות בקורס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: ReportGeneralAttendanceType) => (
                <span
                  style={getAbsenceStyle(Number(rowData.courseAbsenceRate))}
                >
                  {`${Number(rowData.courseAbsenceRate).toFixed(1)}%`}
                </span>
              )}
            />*/}
            {/*            <Column
              field="totalAttendancePercentage"
              header="אחוז הנוכחות בכל התכנית"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: ReportGeneralAttendanceType) => (
                <span
                  style={getAttendanceStyle(
                    Number(rowData.programAttendanceRate),
                  )}
                >
                  {`${Number(rowData.programAttendanceRate).toFixed(1)}%`}
                </span>
              )}
            />*/}
            <Column
              field="studentStatus"
              header="סטטוס תלמיד בתכנית"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: ReportGeneralAttendanceType) => (
                <CustomTag
                  width={'70px'}
                  text={getProgramStudentStatusDisplayText(
                    rowData.programStudentStatus,
                  )}
                  severity={getStatusSeverity(rowData.programStudentStatus)}
                />
              )}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};
